"use client";
import React from "react";
import { useState, useEffect, useRef } from "react";

import { hooks, providers, apijs } from "@lib/client";
import logjs from "@metrojs/logjs";
import Consts from "@common/config/consts";
import { dayjs } from "@lib/client";

// APIクライアントとログインスタンスの初期化
const api = new apijs("api/Common");
const log = new logjs("usePartsList");

/**
 * 詳細画面用のカスタムフック
 * @param {Object} initState - フォームの初期状態（AqbData: 在庫確認データ [PAID] => ... を含められる）
 * @param {Object} state - 追加の状態
 * @param {Object} actions - アクション関数
 * @returns {Array} [form, formProps] - フォームデータとプロパティ
 */
export function usePartsList(initState = {}, state = {}, actions = {}) {
  // フォーム管理フックの使用
  const [form, formProps] = hooks.useFormEx(initState, state, actions);

  async function getBuiByBunruiCd(sbunrui) {
    log.debug("getBuiByBunruiCd START", form);
    actions.showLoading();
    const _params = {
      mode: "getBuiByBunruiCd",
      ...form,
      sbunrui,
    };
    try {
      const result = await api.post(_params);
      log.debug("getBuiByBunruiCd END", result);
      if (result.result === true && result.data) {
        return result.data;
      }
      return null;
    } catch (error) {
      log.error("getBuiByBunruiCd error:", error);
      return { error: error.message, result: false };
    } finally {
      actions.hideLoading();
    }
  }

  async function getBuhinByBuiCd(sbui) {
    log.debug("getBuhinByBuiCd START", form);
    actions.showLoading();
    const _params = {
      mode: "getBuhinByBuiCd",
      ...form,
      bui_cd: sbui,
    };
    try {
      const result = await api.post(_params);
      log.debug("getBuhinByBuiCd END", result);
      if (result.result === true && result.data) {
        return result.data;
      }
      return null;
    } catch (error) {
      log.error("getBuhinByBuiCd error:", error);
      return { error: error.message, result: false };
    } finally {
      actions.hideLoading();
    }
  }
  /**
   * 数値をカンマ区切りでフォーマットする
   * @param {number|string} num - フォーマット対象の数値
   * @returns {string} カンマ区切りの数値文字列
   */
  function formatNumber(num) {
    if (!num && num !== 0) return "";
    return Number(num).toLocaleString();
  }

  /**
   * 日付を y/m/d 形式でフォーマットする（2桁年、先頭0あり）
   * @param {string} dateString - フォーマット対象の日付文字列
   * @returns {string} フォーマット後の日付文字列（y/m/d形式、例: 24/01/01）、無効な場合はnull
   */
  function formatDate(dateString) {
    if (!dateString) return null;
    const d = dayjs(dateString);
    if (!d.isValid()) return null;
    return d.format("YY/MM/DD");
  }

  /**
   * 日付を y/m/d 形式でフォーマットする（2桁年、先頭0なし）
   * @param {string} dateString - フォーマット対象の日付文字列
   * @returns {string} フォーマット後の日付文字列（y/m/d形式、例: 24/1/1）、無効な場合はnull
   */
  function formatDateShort(dateString) {
    if (!dateString) return null;
    const d = dayjs(dateString);
    if (!d.isValid()) return null;
    return d.format("YY/M/D");
  }

  /**
   * 日付を y/m/d H:i迄 形式でフォーマットする
   * @param {string} dateString - フォーマット対象の日付文字列
   * @returns {string} フォーマット後の日付文字列（y/m/d H:i迄形式）、無効な場合はnull
   */
  function formatDateWithTime(dateString) {
    if (!dateString) return null;
    const d = dayjs(dateString);
    if (!d.isValid()) return null;
    return d.format("YY/MM/DD HH:mm迄");
  }

  /**
   * 発注フラグから状態テキストを取得する（bgcolor: "nonegr"版）
   * @param {number} hacchuFlg - 発注フラグ
   * @returns {Object} { jyotai: string, bgcolor: string } 状態テキストと背景色クラス
   */
  function getHacchuStatus(hacchuFlg) {
    log.debug(`getHacchuStatus hacchuFlg:${hacchuFlg}`);
    if (!hacchuFlg && hacchuFlg !== 0) {
      return { jyotai: "", bgcolor: "", view_flg: 0 };
    }

    switch (hacchuFlg) {
      case Consts.HACCHU_FLG.HASOZUMI:
        return { jyotai: "発送済", bgcolor: "", view_flg: 1 };
      case Consts.HACCHU_FLG.KAKUTEI:
        return { jyotai: "注文確定", bgcolor: "", view_flg: 1 };
      case Consts.HACCHU_FLG.NEBIKI:
        return { jyotai: "値引", bgcolor: "", view_flg: 1 };
      case Consts.HACCHU_FLG.HENPIN:
        return { jyotai: "返品", bgcolor: "", view_flg: 1 };
      case Consts.HACCHU_FLG.ZAIKONASHI:
        return { jyotai: "在庫なし回答", bgcolor: "nonegr", view_flg: 1 };
      case Consts.HACCHU_FLG.KIGENGIRE:
        return { jyotai: "在庫回答時間切れ", bgcolor: "nonegr", view_flg: 1 };
      default:
        return { jyotai: "", bgcolor: "", view_flg: 0 };
    }
  }

  /**
   * 発注フラグから状態テキストを取得する（bgcolor: "none"版）
   * @param {number} hacchuFlg - 発注フラグ
   * @returns {Object} { jyotai: string, bgcolor: string } 状態テキストと背景色クラス
   */
  function getHacchuStatusWithNone(hacchuFlg) {
    log.debug(`getHacchuStatusWithNone hacchuFlg:${hacchuFlg}`);
    if (!hacchuFlg && hacchuFlg !== 0) {
      return { jyotai: "", bgcolor: "" };
    }

    switch (hacchuFlg) {
      case Consts.HACCHU_FLG.HASOZUMI:
        return { jyotai: "発送済", bgcolor: "", backgroundColor: "#ffffff" };
      case Consts.HACCHU_FLG.KAKUTEI:
        return { jyotai: "注文確定", bgcolor: "", backgroundColor: "#ffffff" };
      case Consts.HACCHU_FLG.NEBIKI:
        return { jyotai: "値引", bgcolor: "", backgroundColor: "#ffffff" };
      case Consts.HACCHU_FLG.HENPIN:
        return { jyotai: "返品", bgcolor: "", backgroundColor: "#ffffff" };
      case Consts.HACCHU_FLG.ZAIKONASHI:
        return { jyotai: "在庫なし回答", bgcolor: "none", backgroundColor: "#C0C0C0" };
      case Consts.HACCHU_FLG.KIGENGIRE:
        return { jyotai: "在庫回答時間切れ", bgcolor: "none", backgroundColor: "#C0C0C0" };
      default:
        return { jyotai: "", bgcolor: "" };
    }
  }

  /**
   * 発送可能日の値を表示テキストに変換する
   * @param {number} hasso - 発送可能日の値
   * @returns {string} 表示テキスト
   */
  function getHassoStatusText(hasso) {
    log.debug(`getHassoStatusText hasso:${hasso}`, Consts.SELECTION.HASSO_STATUS);
    if (!hasso && hasso !== 0) return "";
    const status = Consts.SELECTION.HASSO_STATUS.find((item) => Number(item.key) === Number(hasso));
    log.debug("status", status);
    log.debug(`getHassoStatusText status:${status?.value}`);
    return status ? status.value : "";
  }

  /**
   * 部品カテゴリのクラス名を取得する
   * @param {number} pkid - 部品カテゴリID
   * @returns {string} クラス名（icon_r, icon_rb, icon_g, icon_e, icon_y）
   */
  function getCategoryClassName(pkid) {
    const categoryMap = {
      1: "icon_r",
      2: "icon_rb",
      3: "icon_g",
      4: "icon_e",
      5: "icon_y",
    };
    return categoryMap[pkid] || "";
  }
  function getCategoryStyle(pkid) {
    const categoryMap = {
      1: { color: "#FFF", backgroundColor: "#C00" },
      2: { color: "#FFF", backgroundColor: "#060" },
      3: { color: "#FFF", backgroundColor: "#606" },
      4: { color: "#FFF", backgroundColor: "#0000FF" },
      5: { color: "#FFF", backgroundColor: "#FFAA00" },
    };

    const _baseStyle = {
      fontSize: "11px",
      padding: "2px",
      display: "inline-block",
      color: "#fff",
      margin: "0 4px",
    };
    return { ..._baseStyle, ...categoryMap[pkid] };
  }
  /**
   * 部品カテゴリ名を取得する
   * @param {number} pkid - 部品カテゴリID
   * @param {Array} partskind - 部品カテゴリマスタ配列
   * @returns {string} 部品カテゴリ名
   */
  function getCategoryName(pkid, partskind) {
    //log.debug(`getCategoryName pkid:${pkid}, partskind:`, partskind);
    if (!pkid || !partskind) return "";
    const category = partskind.find((cat) => Number(cat?.PartsKindID) === Number(pkid));
    //log.debug("category", category);
    return category?.PartsKindName || "";
  }

  /**
   * 依頼期限と見積有効期限のうち、未来（遅い日付）の方を返す
   * @param {string} requestLimit - 依頼期限（Request_Limit）
   * @param {string} sellLimit - 見積有効期限（Sell_Limit）
   * @returns {string}
   */
  function getLaterLimit(requestLimit, sellLimit) {
    if (requestLimit && sellLimit) {
      return dayjs(requestLimit).isAfter(dayjs(sellLimit)) ? requestLimit : sellLimit;
    }
    return requestLimit || sellLimit || "";
  }

  /**
   * 在庫確認ボタンの画像名を取得する（requestLimitあり版）
   * @param {string} paid - 部品回答ID
   * @param {string} requestLimit - 依頼期限
   * @param {Object} AqbData - 在庫確認データ [PAID] => [total, answered, ...]
   * @returns {string} ボタン画像名（空文字列の場合は表示しない）
   */
  function getQabButtonName(paid, requestLimit, AqbData = {}) {
    log.debug(`getQabButtonName paid: ${paid} requestLimit: ${requestLimit} AqbData: `, AqbData);
    if (!requestLimit) {
      return "";
    }
    // 回答を行えるのは見積期限+1ヶ月まで
    const deadline = dayjs(requestLimit).add(1, "month");
    if (!dayjs().isBefore(deadline)) {
      return "";
    }
    const aqbData = AqbData[paid];
    if (!aqbData) {
      return "";
    } else if (!aqbData[2]) {
      return "qa_btn4";
    } else if (aqbData[2] >= aqbData[1]) {
      return "qa_btn5";
    } else {
      return "qa_btn4";
    }
  }

  /**
   * 在庫確認ボタンの画像名を取得する（initState.AqbData 束縛版）
   * @param {string} paid - 部品回答ID
   * @param {string} requestLimit - 依頼期限
   * @returns {string} ボタン画像名（空文字列の場合は表示しない）
   */
  function getQabButtonNameWithAqb(paid, requestLimit) {
    return getQabButtonName(paid, requestLimit, initState.AqbData ?? {});
  }

  /**
   * 在庫確認ボタンの画像名を取得する（requestLimitなし版）
   * @param {number|string} paid - 部品回答ID
   * @param {Object} AqbData - 在庫確認データ [PAID] => [total, answered, ...]
   * @returns {string} ボタン画像名（qa_btn4, qa_btn5, または空文字列）
   */
  function getQabButtonNameSimple(paid, AqbData = {}) {
    if (!paid || !AqbData[paid]) return "";
    const aqbItem = AqbData[paid];
    if (!aqbItem[2]) {
      return "qa_btn4";
    } else if (aqbItem[2] >= aqbItem[1]) {
      return "qa_btn5";
    } else {
      return "qa_btn4";
    }
  }

  /**
   * 未回答用の在庫確認ボタンテキストを取得する
   * @param {string} paid - 部品回答ID
   * @param {Object} AqbData - 在庫確認データ [PAID] => [total, answered, ...]
   * @returns {string} ボタンテキスト（空文字列の場合は表示しない）
   */
  function getQabButtonText(paid, AqbData = {}) {
    if (!paid) return "";
    const aqbData = AqbData[paid];
    if (!aqbData) {
      return "";
    } else if (!aqbData[2]) {
      return "回答する";
    } else {
      return "内容を見る";
    }
  }

  /**
   * 改行文字を<br/>タグに変換する（nl2br相当）
   * @param {string} text - 変換対象のテキスト
   * @returns {JSX.Element[]} JSX要素の配列
   */
  function nl2br(text) {
    if (!text) return "";
    const lines = text.split("\n");
    return lines.map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < lines.length - 1 && <br />}
      </React.Fragment>
    ));
  }

  /**
   * 発送区分の表示テキストを取得する
   * @param {number} haisoKubun - 発送区分
   * @returns {string} 表示テキスト
   */
  function getHaisoKubunText(haisoKubun) {
    if (!haisoKubun && haisoKubun !== 0) return "";
    const status = Consts.SELECTION.HAISO_KUBUN.find((item) => item.key === haisoKubun);
    return status ? status.value : "";
  }

  /**
   * 発送日から経過日数を計算する
   * PHPテンプレート: ceil((strtotime(date('Y-m-d')) - strtotime($partsList[$i]['PartsAnswer']['Haiso_Date'])) / (60 * 60 * 24))
   * @param {string} haisoDate - 発送日（YYYY-MM-DD形式）
   * @returns {number} 経過日数（今日との差分、切り上げ）
   */
  function getDaysSinceHaiso(haisoDate) {
    if (!haisoDate) return null;
    const haiso = dayjs(haisoDate).startOf("day");
    const today = dayjs().startOf("day");
    if (!haiso.isValid()) return null;
    // PHPテンプレート: strtotime(date('Y-m-d')) - strtotime($partsList[$i]['PartsAnswer']['Haiso_Date'])
    // 今日のタイムスタンプから発送日のタイムスタンプを引く（日数に変換）
    // diffは第1引数から第2引数を引いた値を返すので、today.diff(haiso)は正の値（経過日数）
    const diffInDays = today.diff(haiso, "day");
    // PHPテンプレートでは ceil を使用しているので、小数点がある場合は切り上げ
    return Math.ceil(diffInDays);
  }

  /**
   * 発注フラグから在庫確認期限の背景色を取得する
   * @param {number} hacchuFlg - 発注フラグ
   * @returns {string} 背景色（カラーコード）
   */
  function getAnsLimitBgColor(hacchuFlg) {
    if (hacchuFlg === Consts.HACCHU_FLG.KARI) {
      return "#e5ffe5";
    } else if (hacchuFlg === Consts.HACCHU_FLG.ZAIKONASHI || hacchuFlg === Consts.HACCHU_FLG.KIGENGIRE) {
      return "#999999";
    } else if (hacchuFlg === Consts.HACCHU_FLG.KAKUTEI || hacchuFlg === Consts.HACCHU_FLG.HASOZUMI || hacchuFlg === Consts.HACCHU_FLG.NEBIKI || hacchuFlg === Consts.HACCHU_FLG.HENPIN) {
      return "#ffffff";
    } else {
      return "#DCDCDC";
    }
  }

  /**
   * 在庫確認列の表示内容を取得する
   * @param {number} hacchuFlg - 発注フラグ
   * @param {string} bgcolor - 背景色クラス
   * @param {number} index - 行のインデックス（radio nameに使用）
   * @returns {Object} { content: JSX.Element|string, bgColor: string } 表示内容と背景色
   */
  function getZaikoConfirmContent(hacchuFlg, bgcolor, index, setZaikoForm = null) {
    if (hacchuFlg === Consts.HACCHU_FLG.KARI) {
      const _key = "Zaiko-" + index;
      return {
        content: formProps.radio_Zaiko(
          {
            id: _key,
            name: `zaiko-${index}`,
            value: form[_key] || "",
            after_func: (_form) => {
              _form[_key] = _form.Zaiko || Consts.HACCHU_FLG.KAKUTEI;
              log.debug(`radio_Zaiko _form:`, _form);
              formProps.setForm({ ..._form });
              if (setZaikoForm) {
                setZaikoForm(_form);
              }
            },
          },
          false
        ),
      };
    } else if (hacchuFlg === Consts.HACCHU_FLG.KAKUTEI) {
      return { content: "在庫有", bgColor: "" };
    } else if (hacchuFlg === Consts.HACCHU_FLG.ZAIKONASHI) {
      return { content: "在庫無", bgColor: "" };
    } else if (hacchuFlg === Consts.HACCHU_FLG.KIGENGIRE) {
      return { content: "時間切れ", bgColor: "#C0C0C0" };
    } else if (hacchuFlg === Consts.HACCHU_FLG.HASOZUMI || hacchuFlg === Consts.HACCHU_FLG.NEBIKI || hacchuFlg === Consts.HACCHU_FLG.HENPIN) {
      return { content: "発送済", bgColor: "#C0C0C0" };
    } else {
      return { content: "", bgColor: bgcolor || "#DCDCDC" };
    }
  }

  /**
   * 依頼期限がCARRY_TAXIN_DATE以降かどうかを判定する
   * @param {string} requestLimit - 依頼期限（日付文字列）
   * @returns {boolean} 依頼期限がCARRY_TAXIN_DATE以降の場合true
   */
  function isAfterCarryTaxinDate(requestLimit) {
    if (!requestLimit) return false;
    return dayjs(requestLimit).isSameOrAfter(dayjs(Consts.CARRY_TAXIN_DATE));
  }

  /**
   * 入庫済み車輌詳細を新しいウィンドウで開く
   * @param {string|number} genshaId - 現車ID
   */
  function openGenshaDetail(genshaId) {
    if (genshaId) {
      const url = `/gensha_detail/index/${genshaId}/subwin`;
      const height = window.screen.height;
      window.open(url, "photo", `width=800, height=${height}, menubar=no, toolbar=no, scrollbars=yes, resizable=yes`);
    }
  }

  /**
   * 写真ダイアログを開くイベントハンドラーを生成する
   * @param {Object} photoDialogProps - 写真ダイアログのプロパティ（open関数を含む）
   * @param {string} quote_number - 見積番号
   * @param {string|number} prid - 部品依頼ID
   * @param {number} seqnum - シーケンス番号
   * @param {number} refMode - 参照モード（Consts.WRECKER_PHOTOの値）
   * @returns {Function} イベントハンドラー関数
   */
  function handlePhotoClick(photoDialogProps, quote_number, prid, seqnum, refMode, PartsName = "", index = 0) {
    return (e) => {
      e.preventDefault();
      if (photoDialogProps?.open) {
        let isEditMode = false;
        if (refMode === Consts.WRECKER_PHOTO.KARI_WRT) {
          isEditMode = true;
        } else {
          isEditMode = false;
        }
        
        photoDialogProps.open({
          mitsumoriNo: quote_number,
          prid: prid,
          seqnum: seqnum,
          refMode: refMode,
          isEditMode: isEditMode,
          PartsName:PartsName,
          index: index,
        });
      }
    };
  }

  /**
   * ファイルアップロードダイアログを開くイベントハンドラーを生成する
   * @param {Object} fileupDialogProps - ファイルアップロードダイアログのプロパティ（open関数を含む）
   * @param {string} quote_number - 見積番号
   * @param {string|number} prid - 部品依頼ID
   * @param {number} seqnum - シーケンス番号
   * @param {number} refMode - 参照モード（Consts.WRECKER_PHOTOの値）
   * @param {string} [othPartsName] - 他社部品名（オプション、othPartsNameまたはothPNameとして渡される）
   * @returns {Function} イベントハンドラー関数
   */
  function handleFileupClick(fileupDialogProps, quote_number, prid, seqnum, refMode, PartsName = "",) {
    return (e) => {
      e.preventDefault();
      if (fileupDialogProps?.open) {
        let isEditMode = false;
        if (refMode === Consts.WRECKER_PHOTO.KARI_WRT) {
          isEditMode = true;
        } else {
          isEditMode = false;
        }
        const params = {
          mitsumoriNo: quote_number,
          prid: prid,
          seqnum: seqnum,
          refMode: refMode,
          isEditMode: isEditMode,
          PartsName: PartsName,
        };
        fileupDialogProps.open(params);
      }
    };
  }
  function getPrepKindText(prepKind) {
    if (!prepKind && prepKind !== 0) return "";
    if (prepKind === Consts.GOODS.INHOUSE) {
      return "自社品";
    } else if (prepKind === Consts.GOODS.OTHER) {
      return "他社品";
    } else {
      return "";
    }
    const status = Consts.SELECTION.PR;
  }

  function generateHoshoSelectOptions(hoshoSelect) {
    log.debug("generateHoshoSelectOptions ", { hoshoSelect });
    const options = [];
    Object.keys(hoshoSelect).forEach((hs) => {
      options.push({ key: hs, value: hs, label: hoshoSelect[hs] });
    });
    return options;
  }
  return [
    form,
    {
      ...formProps,
      formatNumber,
      formatDate,
      formatDateShort,
      formatDateWithTime,
      getHacchuStatus,
      getHacchuStatusWithNone,
      getHassoStatusText,
      getCategoryClassName,
      getCategoryName,
      getLaterLimit,
      getQabButtonName,
      getQabButtonNameWithAqb,
      getQabButtonNameSimple,
      getQabButtonText,
      nl2br,
      getHaisoKubunText,
      getDaysSinceHaiso,
      getAnsLimitBgColor,
      getZaikoConfirmContent,
      isAfterCarryTaxinDate,
      openGenshaDetail,
      handlePhotoClick,
      handleFileupClick,
      getBuiByBunruiCd,
      getBuhinByBuiCd,
      getCategoryStyle,
      getPrepKindText,
      generateHoshoSelectOptions,
    },
  ];
}
